import { useTranslation } from "react-i18next";
import { Fragment } from "react";
import { UseFormRegister, UseFormWatch } from "react-hook-form";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { GetExtraWorkAgreementQueryResponse, useGetApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdStatusTransitionsQuery } from "api/generatedApi";
import { InputContainer } from "shared/styles/input-container-style";
import { ExtraWorkAgreementFormData } from "../hooks/use-input-validation";
import { useOfflineStorage } from "../../../../../../../utils/offline-storage/use-offline-storage";
import { Box } from "@mui/material";
import { hiddenOnPrint, visibleOnlyOnPrintFlex } from "shared/styles/print-styles";

const compactSelectSx = { '& .MuiSelect-select': { py: 1, px: 1 } };

interface Props {
  agreement?: GetExtraWorkAgreementQueryResponse;
  register: UseFormRegister<ExtraWorkAgreementFormData>;
  watch: UseFormWatch<ExtraWorkAgreementFormData>;
  disabled: boolean;
}

export const approvalStatusMap = new Map([
  ["UnderDevelopment", "dashboard.extraWorkAgreements.approvalStatuses.underDevelopment"],
  ["Approved", "dashboard.extraWorkAgreements.approvalStatuses.approved"],
  ["Rejected", "dashboard.extraWorkAgreements.approvalStatuses.rejected"],
  ["AwaitingApproval", "dashboard.extraWorkAgreements.approvalStatuses.awaitingApproval"],
  ["Cancelled", "dashboard.extraWorkAgreements.approvalStatuses.cancelled"],
]);

export const approvalStatusCommandMap = new Map([
  ["UnderDevelopment", "dashboard.extraWorkAgreements.approvalStatusCommands.underDevelopment"],
  ["Approved", "dashboard.extraWorkAgreements.approvalStatusCommands.approved"],
  ["Rejected", "dashboard.extraWorkAgreements.approvalStatusCommands.rejected"],
  ["AwaitingApproval", "dashboard.extraWorkAgreements.approvalStatusCommands.awaitingApproval"],
  ["Cancelled", "dashboard.extraWorkAgreements.approvalStatusCommands.cancelled"],
]);

export const EditExtraWorkAgreementStatus = (props: Props) => {
  const { agreement, register, disabled, watch } = props;
  const { t } = useTranslation();
  const { getCurrentProjectId } = useOfflineStorage();
  const approvalStatus = watch("approvalStatus") ?? agreement?.approvalStatus;
  const {
    data: approvalStatuses,
    isLoading,
    isFetching,
  } = useGetApiProjectsByProjectIdExtraworkagreementsAndExtraWorkAgreementIdStatusTransitionsQuery(
    {
      extraWorkAgreementId: agreement?.extraWorkAgreementId as string,
      projectId: getCurrentProjectId(),
    },
    { refetchOnMountOrArgChange: true, skip: !agreement?.extraWorkAgreementId }
  );



  if (!agreement) {
    return (
      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, justifyContent: "flex-start", alignItems: "center" }}>
        <Typography variant="caption" color={"primary.main"} >
          {t("captions.approvalStatusEdit")}
        </Typography>

        <Typography variant="body1" sx={{
          color: "secondary.main", borderStyle: "solid", borderWidth: 1,
          borderColor: "secondary.main", borderRadius: 3, px: 1, py: 0.5
        }}>
          {t(approvalStatusMap.get("UnderDevelopment") ?? "")}
        </Typography>
      </Box>
    )
  }

  return (
    <InputContainer>
      <Box sx={[{ display: "flex", flexDirection: "row", gap: 2, justifyContent: "flex-start", alignItems: "center" }, hiddenOnPrint]}>
        <Typography variant="caption" color={"primary.main"} >
          {!disabled ? t("captions.approvalStatus") : t("captions.approvalStatusEdit")}
        </Typography>
        {disabled ? (
          <Typography variant="body1" sx={{
            color: "secondary.main", borderStyle: "solid", borderWidth: 1,
            borderColor: "secondary.main", borderRadius: 3, px: 1, py: 0.5
          }}>
            {t(approvalStatusMap.get(approvalStatus ?? "") ?? "")}
          </Typography>
        ) : (
          <Select
            label="approvalStatus"
            defaultValue={agreement?.approvalStatus}
            {...register("approvalStatus")}
            sx={compactSelectSx}
            renderValue={(value) => {
              if (value === agreement?.approvalStatus) {
                return t(approvalStatusMap.get(value) ?? "");
              }
              return t(approvalStatusCommandMap.get(value) ?? "");
            }}
          >
            {/* Current status as disabled option */}
            <MenuItem disabled value={agreement?.approvalStatus}>
              {t(approvalStatusMap.get(agreement?.approvalStatus ?? "") ?? "")}
            </MenuItem>

            {/* Available transitions */}
            {!isLoading &&
              !isFetching &&
              approvalStatuses?.map((x) => {
                const translation = approvalStatusCommandMap.get(x);
                if (translation != null) {
                  return (
                    <MenuItem key={x} value={x}>
                      {t(approvalStatusCommandMap.get(x) ?? "")}
                    </MenuItem>
                  );
                }
              })}
          </Select>
        )}
      </Box>

      {/*print version below */}
      <Box sx={[{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }, visibleOnlyOnPrintFlex]}>
        <Typography variant="caption" color={"primary.main"} >
          {t("captions.approvalStatusEdit")}
        </Typography>
        <Typography variant="body1">
          {t(approvalStatusMap.get(approvalStatus ?? "") ?? "")}
        </Typography>
      </Box>
    </InputContainer>
  );
};
